import React, { useEffect, useState } from "react";
import {
  ContainerOutlined,
  LineChartOutlined,
  OrderedListOutlined,
  RobotOutlined,
  SettingOutlined,
  SnippetsOutlined,
  TabletOutlined,
} from "@ant-design/icons";
import { Layout, Menu, theme } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Profile from "../components/other/profile";
import { useAdmin } from "context/adminContext";
import { bg_button, bg_text } from "constant/css";
import MerchantHelper from "components/other/merchantHelper";

const { Header, Content, Sider } = Layout;

const siderStyle = {
  overflow: "auto",
  height: "100vh",
  position: "sticky",
  insetInlineStart: 0,
  top: 0,
  bottom: 0,
  scrollbarWidth: "thin",
  scrollbarGutter: "stable",
};

const AdminLayout = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const { accounts } = useAdmin(); // Assuming useAdmin is a custom hook that provides accounts
  const navigate = useNavigate();
  const location = useLocation();
  const items = [
    {
      iconf: (props) => <LineChartOutlined {...props} />,
      icon: <LineChartOutlined />,
      key: "adminDashboard",
      label: "Хянах самбар",
    },
    {
      iconf: (props) => <ContainerOutlined {...props} />,
      icon: <ContainerOutlined />,
      key: "bankTransactionLogs",
      label: "Дансны хуулга",
      children:
        accounts &&
        accounts.map((account) => {
          return {
            iconf: (props) => <ContainerOutlined {...props} />,
            key: `bankTransactionLog/${account.accountNumber}`,
            label: (
              <span className="text-xs">
                {account.accountNumber}
                <span className="text-gray-300">({account.type})</span>
              </span>
            ),
          };
        }),
    },
    {
      iconf: (props) => <SnippetsOutlined {...props} />,
      icon: <SnippetsOutlined />,
      key: "adminInvoice",
      label: "Гүйлгээ",
    },
    {
      iconf: (props) => <SettingOutlined {...props} />,
      icon: <SettingOutlined />,
      key: "adminMerchants",
      label: "Мерчантууд",
    },
    {
      iconf: (props) => <OrderedListOutlined {...props} />,
      icon: <OrderedListOutlined />,
      key: "reference",
      label: "Мэдээллын сан",
      children: [
        {
          iconf: (props) => <RobotOutlined {...props} />,
          key: "functionCall",
          label: (
            <div className="flex items-center relative gap-2">
              <span>AI асуулт</span>
              <span
                className={`text-[8px] flex justify-center px-2 ${bg_button} absolute h-[20px] items-center text-center right-2 font-semibold text-white animate-pulse rounded-md shadow-md`}
              >
                шинэ
              </span>
            </div>
          ),
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "productType",
          label: "Бүтээгдэхүүн",
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "bankCode",
          label: "Банкны код",
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "vatFreeGoods",
          label: "vatFreeGoods",
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "vatZeroGoods",
          label: "vatZeroGoods",
        },
      ],
    },
    {
      iconf: (props) => <TabletOutlined {...props} />,
      icon: <TabletOutlined />,
      key: "pos",
      label: "Пос",
    },
  ];
  const [selected, setSelected] = useState(items[0]);
  const selectedKeys = [selected?.key || items[0].key];

  useEffect(() => {
    const findItemByKey = (key, items) => {
      for (const item of items) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findItemByKey(key, item.children);
          if (found) return found;
        }
      }
      return null;
    };

    const currentPath = location.pathname.replace(/^\//, "");
    const matchedKey = currentPath.startsWith("adminMerchants/")
      ? "adminMerchants"
      : currentPath;
    const currentItem = findItemByKey(matchedKey, items);
    if (currentItem) {
      setSelected(currentItem);
    }
  }, [location.pathname, accounts]);

  const setAndNavigate = (e) => {
    const findItem = (key, items) => {
      for (const item of items) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findItem(key, item.children);
          if (found) return found;
        }
      }
      return null;
    };
    const item = findItem(e.key, items);
    if (item) setSelected(item);
    navigate(e.key);
  };

  return (
    <Layout style={{ height: "100vh" }} hasSider>
      <Sider style={{ ...siderStyle, background: colorBgContainer }}>
        <div className="logoContainer">
          <span className={`${bg_text} font-black`}>Смарт Биллинг Админ</span>
          <span className={`${bg_text} text-xs`}>
            Rapid, Reliable, Rewarding
          </span>
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={selectedKeys}
          onClick={setAndNavigate}
          items={items}
          style={{ borderInlineEnd: "none" }}
        />
        <Profile />
      </Sider>

      <Layout style={{ display: "flex", flexDirection: "column" }}>
        <Header
          style={{
            background: colorBgContainer,
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            height: 64,
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {selected.iconf({ style: { fontSize: 22, color: "#32B7C2" } })}
            {!selected.type && (
              <h2 style={{ margin: 0, fontSize: 18, color: "#32B7C2" }}>
                {selected.label}
              </h2>
            )}
            {selected.type === "merchant" && (
              <h2 style={{ margin: 0, fontSize: 18, color: selected.color }}>
                {selected.slogan}
              </h2>
            )}
          </div>
          {/* Right-side tools if any */}
        </Header>
        <div className="absolute bottom-10 right-10 z-99">
          <MerchantHelper />
        </div>
        <Content
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "5px 12px",
            background: colorBgContainer,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
